import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { isRoomCode, normalizeRoomCode, type Issue, type JoinError } from '@il-colpo/protocol';
import { canPlayOnline } from '@il-colpo/shared/account';
import { useAccount } from '../../account/accountStore.ts';
import { getConnection, onlineAvailable, useConnection } from '../../online/onlineConnection.ts';
import { useFlag } from '../../meta/liveStore.ts';
import { useSettings } from '../../state/settingsStore.ts';
import { Button, Panel, ScreenFrame, TextField } from '../../ui/index.ts';
import { useAccountSession } from '../account/common.tsx';
import styles from './Online.module.css';
import { useSocialBadge } from '../../social/socialStore.ts';
import { IssueLine } from './shared.tsx';

type Gate = 'unavailable' | 'loading' | 'signIn' | 'consent' | 'ready';

/** Whether this player can go online yet, and if not, what stands in the way. */
function useGate(): Gate {
  const { available, load, snapshot } = useAccountSession();
  const consents = useAccount((s) => s.consents);
  if (!available || !onlineAvailable()) return 'unavailable';
  if (load !== 'ready' || snapshot.status === 'loading') return 'loading';
  if (snapshot.status !== 'signedIn' || snapshot.needsSecondFactor) return 'signIn';
  if (consents === null) return 'loading';
  return canPlayOnline(consents) ? 'ready' : 'consent';
}

function GatePanel({ gate }: { gate: Exclude<Gate, 'ready'> }) {
  const { t } = useTranslation('online');
  const navigate = useNavigate();
  if (gate === 'loading') {
    return (
      <Panel>
        <p className={styles['note']} role="status">
          {t('hub.connecting')}
        </p>
      </Panel>
    );
  }
  if (gate === 'unavailable') {
    return (
      <Panel title={t('hub.unavailable')}>
        <p className={styles['note']}>{t('hub.unavailableBody')}</p>
      </Panel>
    );
  }
  return (
    <Panel title={t('hub.signIn')}>
      <div className={styles['stack']}>
        <p className={styles['note']}>
          {gate === 'signIn' ? t('hub.signInBody') : t('hub.consentBody')}
        </p>
        <div>
          <Button
            onClick={() => {
              void navigate('/account');
            }}
          >
            {t('hub.signInAction')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/**
 * After a few seconds of waiting for the server: one on a free plan sleeps when nobody has used it for a while and
 * takes up to a minute to wake, which a player would take for a fault.
 */
export function WakingNote({ pending }: { pending: boolean }) {
  const { t } = useTranslation('online');
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!pending) return;
    const id = setTimeout(() => {
      setSlow(true);
    }, 4000);
    return () => {
      clearTimeout(id);
      setSlow(false);
    };
  }, [pending]);
  return pending && slow ? (
    <p className={styles['note']} role="status" data-testid="waking">
      {t('hub.waking')}
    </p>
  ) : null;
}

/** Runs a join, and goes to the room when it works. */
function useJoin() {
  const navigate = useNavigate();
  const [pending, setPending] = useState<string | null>(null);
  const [issue, setIssue] = useState<Issue | null>(null);
  const [elsewhere, setElsewhere] = useState<string | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const run = async (kind: string, join: () => Promise<void>): Promise<void> => {
    setPending(kind);
    setIssue(null);
    setElsewhere(null);
    setDetail(null);
    try {
      await join();
      void navigate('/online/room');
    } catch (error) {
      const failed = error as Partial<JoinError>;
      setIssue(failed.issue ?? 'unreachable');
      // The server says where the player already sits: a way back, for a page that was reloaded.
      if (failed.issue === 'alreadyInRoom' && failed.detail) setElsewhere(failed.detail);
      setDetail(failed.detail ?? null);
    } finally {
      setPending(null);
    }
  };
  return { pending, issue, detail, elsewhere, run };
}

/** The way back to the table the player already sits at, when a join was refused for that. */
function ResumePanel({
  room,
  pending,
  onResume,
}: {
  room: string | null;
  pending: boolean;
  onResume: (room: string) => void;
}) {
  const { t } = useTranslation('online');
  if (!room) return null;
  return (
    <Panel>
      <div className={styles['stack']}>
        <p className={styles['note']}>{t('hub.resumeHint')}</p>
        <div>
          <Button
            loading={pending}
            onClick={() => {
              onResume(room);
            }}
          >
            {t('hub.resume')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}

function Tile({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <div className={styles['tile']}>
      <h3>{title}</h3>
      <p>{hint}</p>
      {children}
    </div>
  );
}

/** `/online`: a quick match, a private room, or a room by its code. */
export function OnlineHub() {
  const { t } = useTranslation('online');
  const gate = useGate();
  const { language } = useSettings();
  const state = useConnection();
  const navigate = useNavigate();
  const { pending, issue, detail, elsewhere, run } = useJoin();
  const [code, setCode] = useState('');
  const rankedOn = useFlag('ranked');
  const friendsOn = useFlag('friends');
  const waiting = useSocialBadge();
  const guest = useAccount((s) => s.snapshot.user?.anonymous ?? false);
  const normalized = normalizeRoomCode(code);
  const inRoom = ['lobby', 'playing', 'over', 'reconnecting'].includes(state.status);

  const join = (e: FormEvent) => {
    e.preventDefault();
    if (isRoomCode(normalized)) void run('join', () => getConnection().joinRoom(normalized));
  };

  return (
    <ScreenFrame title={t('title')} backTo="/">
      <div className={styles['page']}>
        <p className={styles['intro']}>{t('hub.intro')}</p>
        {gate !== 'ready' ? <GatePanel gate={gate} /> : null}
        <WakingNote pending={pending !== null} />
        {gate === 'ready' && inRoom ? (
          <Panel>
            <div className={styles['row']}>
              <span className={styles['note']}>{t('hub.backHint')}</span>
              <Button
                onClick={() => {
                  void navigate(
                    state.status === 'playing' || state.status === 'over'
                      ? '/game'
                      : '/online/room',
                  );
                }}
              >
                {t('hub.back')}
              </Button>
            </div>
          </Panel>
        ) : null}
        {gate === 'ready' && !inRoom ? (
          <>
            <div className={styles['tiles']}>
              <Tile title={t('hub.quick')} hint={t('hub.quickHint')}>
                <Button
                  icon="play"
                  loading={pending === 'quick'}
                  onClick={() => {
                    void run('quick', () => getConnection().quickMatch());
                  }}
                >
                  {t('hub.quick')}
                </Button>
              </Tile>
              {rankedOn ? (
                <Tile
                  title={t('hub.ranked')}
                  hint={guest ? t('hub.rankedGuest') : t('hub.rankedHint')}
                >
                  <Button
                    variant="secondary"
                    icon="star"
                    disabled={guest}
                    loading={pending === 'ranked'}
                    onClick={() => {
                      void run('ranked', () => getConnection().rankedMatch());
                    }}
                  >
                    {t('hub.ranked')}
                  </Button>
                </Tile>
              ) : null}
              <Tile title={t('hub.create')} hint={t('hub.createHint')}>
                <Button
                  variant="secondary"
                  icon="key"
                  loading={pending === 'create'}
                  onClick={() => {
                    void run('create', () => getConnection().createRoom({ language }));
                  }}
                >
                  {t('hub.create')}
                </Button>
              </Tile>
            </div>
            <Panel title={t('hub.join')}>
              <form className={styles['stack']} onSubmit={join} noValidate>
                <TextField
                  label={t('hub.codeLabel')}
                  hint={t('hub.codeHint')}
                  value={code}
                  maxLength={12}
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  onChange={setCode}
                />
                <div>
                  <Button
                    type="submit"
                    loading={pending === 'join'}
                    disabled={!isRoomCode(normalized)}
                  >
                    {t('hub.joinAction')}
                  </Button>
                </div>
              </form>
            </Panel>
            {friendsOn ? (
              <Panel title={t('hub.friends')}>
                <div className={styles['row']}>
                  <span className={styles['note']}>
                    {guest ? t('hub.friendsGuest') : t('hub.friendsHint')}
                  </span>
                  <Button
                    variant="secondary"
                    icon="profile"
                    disabled={guest}
                    onClick={() => {
                      void navigate('/friends');
                    }}
                  >
                    {t('hub.friendsOpen')}
                    {waiting > 0 ? ` (${String(waiting)})` : ''}
                  </Button>
                </div>
              </Panel>
            ) : null}
            <IssueLine issue={issue} detail={detail} />
            <ResumePanel
              room={elsewhere}
              pending={pending === 'resume'}
              onResume={(room) => {
                void run('resume', () => getConnection().joinRoom(room));
              }}
            />
          </>
        ) : null}
      </div>
    </ScreenFrame>
  );
}

/** `/online/join/:code`: an invite link, followed. */
export function OnlineJoin() {
  const { t } = useTranslation('online');
  const { code = '' } = useParams();
  const gate = useGate();
  const state = useConnection();
  const { pending, issue, elsewhere, run } = useJoin();
  const tried = useRef(false);
  const navigate = useNavigate();
  const wanted = normalizeRoomCode(code);

  useEffect(() => {
    if (gate !== 'ready' || tried.current) return;
    tried.current = true;
    if (['lobby', 'playing', 'over', 'reconnecting'].includes(state.status)) {
      void navigate('/online/room', { replace: true });
      return;
    }
    if (isRoomCode(wanted)) void run('join', () => getConnection().joinRoom(wanted));
  }, [gate, wanted, state.status, navigate, run]);

  return (
    <ScreenFrame title={t('title')} backTo="/online">
      <div className={styles['page']}>
        {gate !== 'ready' ? <GatePanel gate={gate} /> : null}
        {gate === 'ready' && issue === null ? (
          <Panel>
            <p className={styles['note']} role="status">
              {pending ? t('hub.connecting') : null}
            </p>
            <WakingNote pending={pending !== null} />
          </Panel>
        ) : null}
        {!isRoomCode(wanted) ? <IssueLine issue="notFound" /> : <IssueLine issue={issue} />}
        <ResumePanel
          room={elsewhere}
          pending={pending === 'resume'}
          onResume={(room) => {
            void run('resume', () => getConnection().joinRoom(room));
          }}
        />
        {issue || !isRoomCode(wanted) ? (
          <Link className={styles['note']} to="/online">
            {t('game.backToOnline')}
          </Link>
        ) : null}
      </div>
    </ScreenFrame>
  );
}
